import { create } from 'zustand';
import type { DataRow, ExcelData } from '@/types';
import { EXCEL_FIELDS } from '@/lib/constants';
import { useDataStore } from '@/store/useDataStore';
import {
    clearLegacyConfigStorage,
    hydrateConfigPreferences,
    persistCurrentConfigPreferences,
    setConfigPersistenceHandler,
    type ConfigPreferences,
} from '@/store/useConfigStore';

export interface CollectionBoxSaveError {
    item_id: number;
    message: string;
}

export interface CollectionBoxSaveResult {
    success_count: number;
    failed_count: number;
    remaining_count: number;
    items: Array<{ item_id: number; updated_at: string; deleted_sku_count: number }>;
    errors: CollectionBoxSaveError[];
}

export interface CollectionBoxFilters {
    keyword: string;
    source_item_id: string;
    source_platform: string;
    price_min: string;
    price_max: string;
    weight_min: string;
    weight_max: string;
    group_id: string;
    notes: string;
    is_claimed_shopee: string;
    claim_platform: string;
    time_field: 'created_at' | 'updated_at';
    time_start: string;
    time_end: string;
}

export interface CollectionBoxGroupOption {
    id: number;
    name: string;
}

export interface CollectionBoxPagination {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
}

interface SourceState {
    mode: 'excel' | 'collection-box';
    collectionStatus: 'unprocessed_unclaimed' | 'unprocessed_claimed' | 'processed_unclaimed' | 'processed_claimed';
    sourcePlatforms: string[];
    groupOptions: CollectionBoxGroupOption[];
    pagination: CollectionBoxPagination;
    loading: boolean;
    saving: boolean;
    saveResult: CollectionBoxSaveResult | null;
    saveError: string;
    setMode: (mode: SourceState['mode']) => void;
    setCollectionStatus: (status: SourceState['collectionStatus']) => void;
    setSourcePlatforms: (platforms: string[]) => void;
    setGroupOptions: (groups: CollectionBoxGroupOption[]) => void;
    setPagination: (pagination: CollectionBoxPagination) => void;
    setLoading: (loading: boolean) => void;
    setSaving: (saving: boolean) => void;
    setSaveResult: (result: CollectionBoxSaveResult | null) => void;
    setSaveError: (error: string) => void;
}

export const useSourceStore = create<SourceState>((set) => ({
    mode: 'excel',
    collectionStatus: 'unprocessed_unclaimed',
    sourcePlatforms: [],
    groupOptions: [],
    pagination: { total: 0, per_page: 10, current_page: 1, last_page: 1, from: 0, to: 0 },
    loading: false,
    saving: false,
    saveResult: null,
    saveError: '',
    setMode: (mode) => set({ mode }),
    setCollectionStatus: (collectionStatus) => set({ collectionStatus }),
    setSourcePlatforms: (sourcePlatforms) => set({ sourcePlatforms }),
    setGroupOptions: (groupOptions) => set({ groupOptions }),
    setPagination: (pagination) => set({ pagination }),
    setLoading: (loading) => set({ loading }),
    setSaving: (saving) => set({ saving }),
    setSaveResult: (saveResult) => set({ saveResult }),
    setSaveError: (saveError) => set({ saveError }),
}));

let parentOrigin = '';
let requestSequence = 0;
const pendingSaves = new Map<string, {
    resolve: (result: CollectionBoxSaveResult) => void;
    reject: (error: Error) => void;
}>();
const pendingBrowserTasks = new Map<string, {
    resolve: (result: unknown) => void;
    reject: (error: Error) => void;
}>();

function applySaveResult(result: CollectionBoxSaveResult) {
    const successful = new Map(result.items.map(item => [String(item.item_id), item]));
    if (successful.size === 0) return;

    const state = useDataStore.getState();
    const sourceState = useSourceStore.getState();
    const removeSuccessfulItems = sourceState.collectionStatus.startsWith('unprocessed_');
    const rows: DataRow[] = [];
    const originalRows: DataRow[] = [];
    const filteredRowIndices = new Set<number>();

    state.rows.forEach((row, oldIndex) => {
        const item = successful.get(String(row.__commonItemId ?? ''));
        const wasFiltered = state.filteredRowIndices.has(oldIndex);
        if (item && removeSuccessfulItems) return;
        if (item && wasFiltered) return;

        const nextRow = item ? { ...row, __commonItemUpdatedAt: item.updated_at } : row;
        const nextIndex = rows.length;
        rows.push(nextRow);
        originalRows.push(item ? { ...nextRow } : { ...(state.originalRows[oldIndex] ?? nextRow) });
        if (!item && wasFiltered) filteredRowIndices.add(nextIndex);
    });

    useDataStore.setState({ rows, originalRows, filteredRowIndices });
    if (removeSuccessfulItems) {
        const removed = successful.size;
        const total = Math.max(0, sourceState.pagination.total - removed);
        sourceState.setPagination({
            ...sourceState.pagination,
            total,
            to: Math.max(sourceState.pagination.from ? sourceState.pagination.from - 1 : 0, sourceState.pagination.to - removed),
            last_page: Math.max(1, Math.ceil(total / sourceState.pagination.per_page)),
        });
    }
}

export function initializeCollectionBoxBridge(importData: (data: ExcelData) => void): (() => void) | undefined {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embed') !== 'collection-box' || window.parent === window) return undefined;

    parentOrigin = params.get('parent_origin') || '';
    if (!/^https?:\/\//i.test(parentOrigin)) return undefined;

    useSourceStore.getState().setMode('collection-box');
    setConfigPersistenceHandler((preferences) => {
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:preferences-save',
            payload: preferences,
        }, parentOrigin);
    });

    const onMessage = (event: MessageEvent) => {
        if (event.origin !== parentOrigin || event.source !== window.parent || event.data?.source !== 'tshopee') return;

        if (event.data.type === 'collection-box:data') {
            const payload = event.data.payload ?? {};
            const validStatuses = ['unprocessed_unclaimed', 'unprocessed_claimed', 'processed_unclaimed', 'processed_claimed'] as const;
            const status = validStatuses.includes(payload.status)
                ? payload.status
                : 'unprocessed_unclaimed';
            useSourceStore.getState().setCollectionStatus(status);
            useSourceStore.getState().setSourcePlatforms(Array.isArray(payload.sourcePlatforms) ? payload.sourcePlatforms.map(String) : []);
            useSourceStore.getState().setGroupOptions(Array.isArray(payload.groups) ? payload.groups : []);
            useSourceStore.getState().setPagination({
                total: Number(payload.pagination?.total ?? 0),
                per_page: Number(payload.pagination?.per_page ?? 10),
                current_page: Number(payload.pagination?.current_page ?? 1),
                last_page: Number(payload.pagination?.last_page ?? 1),
                from: Number(payload.pagination?.from ?? 0),
                to: Number(payload.pagination?.to ?? 0),
            });
            useSourceStore.getState().setLoading(false);
            importData({
                description: String(payload.description ?? ''),
                headers: EXCEL_FIELDS,
                rows: Array.isArray(payload.rows) ? payload.rows : [],
                fileName: String(payload.fileName ?? 'AI采集箱.xlsx'),
            });
            window.dispatchEvent(new CustomEvent('collection-box:data-loaded'));
            return;
        }

        if (event.data.type === 'collection-box:preferences') {
            const preferences = event.data.payload;
            if (preferences && typeof preferences === 'object' && !Array.isArray(preferences)) {
                hydrateConfigPreferences(preferences as Partial<ConfigPreferences>);
                clearLegacyConfigStorage();
            } else {
                persistCurrentConfigPreferences();
            }
            return;
        }

        if (event.data.type === 'collection-box:preferences-saved') {
            clearLegacyConfigStorage();
            return;
        }

        if (event.data.type === 'collection-box:preferences-error') {
            console.error(String(event.data.message ?? 'AI 采集箱配置保存失败'));
            return;
        }

        if (event.data.type === 'collection-box:error') {
            useSourceStore.getState().setLoading(false);
            useSourceStore.getState().setSaveError(String(event.data.message ?? 'AI 采集箱载入失败'));
            return;
        }

        if (event.data.type === 'collection-box:save-result') {
            const callback = pendingSaves.get(String(event.data.requestId ?? ''));
            if (!callback) return;
            pendingSaves.delete(String(event.data.requestId));
            if (event.data.error) callback.reject(new Error(String(event.data.error)));
            else callback.resolve(event.data.payload as CollectionBoxSaveResult);
            return;
        }

        if (event.data.type === 'collection-box:browser-task-result') {
            const callback = pendingBrowserTasks.get(String(event.data.requestId ?? ''));
            if (!callback) return;
            pendingBrowserTasks.delete(String(event.data.requestId));
            if (event.data.error) callback.reject(new Error(String(event.data.error)));
            else callback.resolve(event.data.payload);
        }
    };

    window.addEventListener('message', onMessage);
    window.parent.postMessage({ source: 'tabbygrid', type: 'collection-box:ready' }, parentOrigin);

    return () => {
        window.removeEventListener('message', onMessage);
        setConfigPersistenceHandler(null);
    };
}

function requestBrowserTask(type: 'load' | 'checkpoint' | 'delete', payload?: unknown, keepalive = false): Promise<unknown> {
    if (!parentOrigin || window.parent === window) return Promise.reject(new Error('AI 采集箱数据桥接未初始化'));
    const requestId = `browser-task-${Date.now()}-${++requestSequence}`;
    return new Promise((resolve, reject) => {
        pendingBrowserTasks.set(requestId, { resolve, reject });
        window.parent.postMessage({
            source: 'tabbygrid',
            type: `collection-box:browser-task-${type}`,
            requestId,
            payload,
            keepalive,
        }, parentOrigin);
    });
}

export async function loadServerBrowserTask<T>(): Promise<T | null> {
    return await requestBrowserTask('load') as T | null;
}

export async function checkpointServerBrowserTask(task: unknown, keepalive = false): Promise<void> {
    await requestBrowserTask('checkpoint', task, keepalive);
}

export async function deleteServerBrowserTask(keepalive = false): Promise<void> {
    await requestBrowserTask('delete', undefined, keepalive);
}

export function loadCollectionBox(filters: CollectionBoxFilters, page = 1, perPage = 10): void {
    if (!parentOrigin || window.parent === window) return;
    useSourceStore.getState().setLoading(true);
    useSourceStore.getState().setSaveError('');
    window.parent.postMessage({
        source: 'tabbygrid',
        type: 'collection-box:load',
        payload: { ...filters, page, per_page: perPage },
    }, parentOrigin);
}

export async function saveCollectionBox(rows: DataRow[], filteredRowIndices: Set<number>): Promise<CollectionBoxSaveResult> {
    if (!parentOrigin || window.parent === window) throw new Error('AI 采集箱数据桥接未初始化');

    const requestId = `save-${Date.now()}-${++requestSequence}`;
    const result = await new Promise<CollectionBoxSaveResult>((resolve, reject) => {
        pendingSaves.set(requestId, { resolve, reject });
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:save',
            requestId,
            payload: {
                rows,
                filteredRowIndices: Array.from(filteredRowIndices),
            },
        }, parentOrigin);
    });

    applySaveResult(result);
    return result;
}
